import { Field, FieldProps, Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { TextField, Typography } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MenuItem from "@mui/material/MenuItem";
import colors from "assets/theme-dark/base/colors";
import { CRUXY_ADMIN, CRUXY_USER } from "common/names";
import { User } from "@/graphql/types/graphql";

interface Cube {
  cubeId: string;
  name: string;
}

interface CubeFormValue {
  name: string;
}

interface CubeManagementFormProps {
  user: User;
  cubes: Cube[] | undefined;
  selectedCubeId: string | null;
  errorCubeName: boolean;
  cubesLoading: boolean;
  deleteLoading: boolean;
  setErrorCubeName: (error: boolean) => void;
  setErrorCompany: (error: string) => void;
  setSelectedCubeId: (cubeId: string | null) => void;
  setOpenDeleteDialog: (open: boolean) => void;
  newCubeHandler: (values: CubeFormValue, { resetForm }: { resetForm: () => void }) => Promise<void>;
}

const newCubeSchema = Yup.object().shape({
  name: Yup.string().required("Name required!"),
});

function SubmitButton() {
  const { values } = useFormikContext<CubeFormValue>();
  return (
    <MDButton
      type="submit"
      variant="background"
      size="large"
      fullWidth
      disabled={!values.name}
      sx={{ borderRadius: "4px" }}
    >
      Add Cube
    </MDButton>
  );
}

function CubeManagementForm({
  user,
  cubes,
  selectedCubeId,
  errorCubeName,
  cubesLoading,
  deleteLoading,
  setErrorCubeName,
  setErrorCompany,
  setSelectedCubeId,
  setOpenDeleteDialog,
  newCubeHandler,
}: CubeManagementFormProps): JSX.Element {
  return (
    <>
      <Typography
        sx={{
          color: colors.text.main,
          fontSize: "1.5rem",
          fontWeight: 600,
          marginBottom: "1rem",
        }}
      >
        New Cube
      </Typography>
      <Formik
        initialValues={{
          name: "",
        }}
        validationSchema={newCubeSchema}
        onSubmit={newCubeHandler}
      >
        <Form style={{ width: "100%" }}>
          {Boolean(user.role === CRUXY_ADMIN || user.role === CRUXY_USER) && (
            <Field sx={{ width: "100%" }} name="name">
              {(props: FieldProps<string, CubeFormValue>) => (
                <TextField
                  size="small"
                  onBlur={() => setErrorCubeName(true)}
                  inputProps={{ "data-testid": "cubeNameForm", style: { color: colors.primary.main } }}
                  sx={{ width: "100%", marginBottom: "1.5rem" }}
                  name="name"
                  label={"Cube Name"}
                  color="secondary"
                  autoFocus
                  required
                  value={props.field.value}
                  onChange={(e) => {
                    setErrorCubeName(false);
                    setErrorCompany(" ");
                    props.form.setFieldValue(props.field.name, e.target.value, true);
                  }}
                />
              )}
            </Field>
          )}

          <MDBox mb={1}>
            <SubmitButton />
            <Typography
              sx={{
                color: colors.text.main,
                fontSize: "1.5rem",
                fontWeight: 600,
                marginTop: "1rem",
              }}
            >
              Delete Cube
            </Typography>
            <MDBox sx={{ display: "flex", gap: 2, marginTop: "1rem" }}>
              <TextField
                size="small"
                select
                label="Select a cube to delete"
                value={selectedCubeId || ""}
                onChange={(e) => setSelectedCubeId(e.target.value)}
                sx={{
                  width: "50%",
                  "& .MuiSelect-select": {
                    color: colors.primary.main,
                  },
                }}
                disabled={cubesLoading || !cubes}
              >
                {cubes?.map((cube: Cube) => (
                  <MenuItem key={cube.cubeId} value={cube.cubeId}>
                    {cube.name}
                  </MenuItem>
                ))}
                <MenuItem value="">None</MenuItem>
              </TextField>
              <MDButton
                variant="contained"
                size="small"
                onClick={() => setOpenDeleteDialog(true)}
                disabled={!selectedCubeId || deleteLoading}
                sx={{ width: "50%", backgroundColor: "red" }}
              >
                Delete Cube
              </MDButton>
            </MDBox>
          </MDBox>
        </Form>
      </Formik>
    </>
  );
}

export default CubeManagementForm;
